import { CloudUploadIcon, FileIcon, Trash2Icon } from "lucide-react";
import { Button } from "./button";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFetch } from "@/src/lib/useFetch";
import { useDrop } from "react-dnd";
import { NativeTypes } from "react-dnd-html5-backend";
import { cn } from "@/src/lib/utils";
import { FileData } from "@/src/lib/schemas";
import { Sortable, SortableItem } from "./sortable";

export type RenderFilesProps = {
  files: FileData[];
  onChange?: (files: FileData[]) => void;
  remove: (fileId: string) => void;
};

export type UploadInputProps = {
  accept?: string;
  className?: string;
  ownerType: string;
  ownerId?: string;
  renderFiles?: (control: RenderFilesProps) => React.ReactNode;
} & (
  | {
      multiple?: false;
      value?: FileData | null;
      onChange?: (file: FileData | null) => void;
    }
  | {
      multiple: true;
      value?: FileData[];
      onChange?: (files: FileData[]) => void;
    }
);

export function UploadInput(props: UploadInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { fetch, isFetching } = useFetch();
  const value = props.value ? (props.multiple ? props.value : [props.value]) : [];

  useEffect(() => {
    // this one fix the issue with adding new boarding info file
    props.value && setFiles(props.multiple ? props.value : [props.value]);
  }, [props.value]);

  const uploadFile = useCallback(
    async (file: File) => {
      const data = new FormData();
      data.append("file", file);
      data.append("ownerType", props.ownerType);
      if (props.ownerId) {
        data.append("ownerId", props.ownerId);
      }
      const res = await fetch<{ status: "success"; data: NonNullable<FileData> }>("/api/files/upload", "POST", data);

      return res.data;
    },
    [fetch],
  );
  const [, dropRef] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop: async (item: any) => {
        const file = item?.files?.[0] as File | undefined;
        if (file) {
          const res = await uploadFile(file);

          if (files.find((e) => e.id === res.id)) {
            return;
          }

          const nextFile = { id: res.id, url: res.url, name: file.name };
          const nextFiles = [...files, nextFile];
          setFiles(nextFiles);
          if (props.multiple) {
            props.onChange?.(nextFiles);
          } else {
            props.onChange?.(nextFile);
          }
        }
      },
    }),
    [uploadFile],
  );

  const [files, setFiles] = useState<NonNullable<FileData>[]>(value);

  const removeFile = (fileId: string) => {
    const nextFiles = files.filter((f) => f.id !== fileId);
    setFiles(nextFiles);
    if (props.multiple) {
      props.onChange?.(nextFiles);
    } else {
      props.onChange?.(null);
    }
  };

  return (
    <div className="flex flex-col items-start gap-1" ref={dropRef}>
      <div className={cn("p-4 border border-slate-300 flex flex-col items-center w-full rounded-md", props.className)}>
        <CloudUploadIcon size={24} className="text-slate-900" />
        <span className="text-slate-900">
          <Button
            disabled={isFetching}
            type="button"
            variant="link"
            size="sm"
            className="p-0"
            onClick={() => fileInputRef.current?.click()}
          >
            Click to upload file
          </Button>{" "}
          or drag and drop
        </span>
        <span className="text-slate-600 text-xs">{acceptHint(props.accept)}</span>
        <input
          type="file"
          className="hidden"
          accept={props.accept}
          multiple={false} // TODO: implement multiple file upload
          ref={fileInputRef}
          onChange={async (ev) => {
            ev.preventDefault();
            const _files = ev.currentTarget.files ? [...ev.currentTarget.files] : [];
            const firstFile = _files[0];

            const res = await uploadFile(firstFile);

            const nextFile = { id: res.id, url: res.url, name: firstFile.name };
            const nextFiles = [...files, nextFile];
            setFiles(nextFiles);
            if (props.multiple) {
              props.onChange?.(nextFiles);
            } else {
              props.onChange?.(nextFile);
            }
          }}
        />
      </div>
      {props.renderFiles ? (
        props.renderFiles({
          files,
          onChange: (l) => {
            setFiles(l);
            if (props.multiple) {
              props.onChange?.(l);
            } else {
              props.onChange?.(l[0] ?? null);
            }
          },
          remove: removeFile,
        })
      ) : (
        <FilesList files={files} remove={removeFile} />
      )}
    </div>
  );
}

export function FilesList(props: RenderFilesProps) {
  return (
    <div className="flex flex-col gap-1 text-slate-600">
      {props.files.map((file) => (
        <div key={file.id} className="flex items-center gap-1">
          <FileIcon size={20} className="w-5" />
          <span className="flex-1">{file.name}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="pl-1 pr-1 m-0"
            onClick={() => props.remove(file.id)}
          >
            <Trash2Icon />
          </Button>
        </div>
      ))}
    </div>
  );
}

export function ImagesPreview(props: RenderFilesProps) {
  return (
    <div className="flex flex-wrap gap-4 items-center">
      <Sortable orientation="mixed" value={props.files} onValueChange={props.onChange}>
        {props.files.map((f) => (
          <SortableItem key={f.id} value={f.id} asTrigger>
            <div className="relative h-32 w-48 overflow-hidden rounded-md border border-slate-300">
              <img src={f.url} alt={f.name} className="object-contain h-full w-full cursor-grab" draggable={false} />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-1 right-1 p-1 bg-[rgba(248,250,252,0.5)] cursor-pointer"
                onClickCapture={(e) => {
                  props.remove(f.id);
                }}
              >
                <Trash2Icon />
              </Button>
            </div>
          </SortableItem>
        ))}
      </Sortable>
    </div>
  );
}

function acceptHint(accept: string = "") {
  const mimeTypes = accept.split(",").map((m) => m.trim());

  const ext = (m: string) =>
    m
      .replace(/.*\//, "")
      .toUpperCase()
      .replace(/SVG\+XML/, "SVG");

  if (mimeTypes.length === 0 || mimeTypes[0] === "" || mimeTypes[0] === "*" || mimeTypes[0] === "*/*") {
    return "Any file type";
  }
  if (mimeTypes.length === 1) {
    return ext(mimeTypes[0]);
  }
  if (mimeTypes.length === 2) {
    return `${ext(mimeTypes[0])} or ${ext(mimeTypes[1])}`;
  }
  const last = ext(mimeTypes.pop()!);

  return mimeTypes.map(ext).join(",") + `, or ${last}`;
}
